import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { APP_INITIALIZER, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { Router, RouterModule } from '@angular/router'
import { ContentEditableModule } from '@containers/i18n/content-editable/content-editable.module'
import { I18nModule } from '@containers/i18n/i18n.module'
import { I18nService } from '@containers/i18n/i18n.service'
import { i18nInitialaizer } from '@containers/i18n/initialazier'
import { LanguageService } from '@containers/i18n/language.service'
import { MissingTranslationHandlerService } from '@containers/i18n/missing-translation-handler.service'
import { MoptaxTranslateLoader } from '@containers/i18n/translate.loader'
import { LanguagesModule } from '@containers/languages/languages.module'
import { EffectsModule } from '@ngrx/effects'
import {
  MetaReducer,
  Store,
  StoreModule,
  USER_PROVIDED_META_REDUCERS,
} from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
  MissingTranslationHandler as ngxMissingTranslationHandler,
} from '@ngx-translate/core'
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2'
import { NgxPermissionsModule } from 'ngx-permissions'
import { ToastrModule } from 'ngx-toastr'
import { AuthGuard } from 'src/app/guards/auth.guard'
import { reducers } from '../app/app.reducers'
import { AppComponent } from './app.component'
import { effects } from './app.effects'
import { metaReducers, refreshToken } from './app.meta-reducers'
import { routes } from './app.routes'
import { services } from './app.services'
import { AuthLayoutComponent } from './components/auth-layout/auth-layout.component'
import { MainLayoutComponent } from './components/main-layout/main-layout.component'
import { AdminModule } from './containers/admin/admin.module'
import { AuditLogsModule } from './containers/audit-logs/audit-logs.module'
import { AuthModule } from './containers/auth/auth.module'
import { AuthService } from './containers/auth/auth.service'
import { BusinessesModule } from './containers/businesses/businesses.module'
import { CommercialLicenseMappingsModule } from './containers/commercial-license-mappings/commercial-license-mappings.module'
import { CommunitiesModule } from './containers/communities/communities.module'
import { ContactTitlesModule } from './containers/contact-titles/contact-titles.module'
import { ContactTypesModule } from './containers/contact-types/contact-types.module'
import { DashboardModule } from './containers/dashboard/dashboard.module'
import { DataCyclesModule } from './containers/data-cycles/data-cycles.module'
import { DiscountGroupsModule } from './containers/discount-groups/discount-groups.module'
import { DocumentTypesModule } from './containers/document-types/document-types.module'
import { FinancialInstitutionsModule } from './containers/financial-institutions/financial-institutions.module'
import { FixedTemplatesModule } from './containers/fixed-templates/fixed-templates.module'
import { FormsModule } from './containers/forms/forms.module'
import { ImageTypesModule } from './containers/image-types/image-types.module'
import { IndustriesModule } from './containers/industries/industries.module'
import { MapModule } from './containers/map/map.module'
import { OrganizationsModule } from './containers/organizations/organizations.module'
import { PaymentPurposesModule } from './containers/payment-purposes/payment-purposes.module'
import { ProfileModule } from './containers/profile/profile.module'
import { PropertiesModule } from './containers/properties/properties.module'
import { PropertyTypeTemplatesModule } from './containers/property-type-templates/property-type-templates.module'
import { PdfBatchGenerationModule } from './containers/pdf-batch-generation/pdf-batch-generation.module'
import { ReportsModule } from './containers/reports/reports.module'
import { RolesModule } from './containers/roles/roles.module'
import { SearchModule } from './containers/search-results/search-results.module'
import { SettingsModule } from './containers/settings/settings.module'
import { StreetTypesModule } from './containers/street-types/street-types.module'
import { StreetsModule } from './containers/streets/streets.module'
import { TemplatesModule } from './containers/templates/templates.module'
import { UsersModule } from './containers/users/users.module'
import { WardsModule } from './containers/wards/wards.module'
import { GuestGuard } from './guards/guest.guard'
import { PermissionsGuard } from './guards/permissions.guard'
import { RedirectGuard } from './guards/redirect.guard'
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor'
import { TokenInterceptor } from './interceptors/token.interceptor'
import { SharedModule } from './shared/shared.module'
import { SharedService } from './shared/shared.service'
import { BusinessCategoriesModule } from '@containers/business-categories/business-categories.module'
import { SystemSettingsModule } from '@containers/system-settings/system-settings.module'
import { SecuritySettingsModule } from '@containers/security-settings/security-settings.module'
import { SmtpSettingsModule } from '@containers/smtp-settings/smtp-settings.module'
import { GisSettingsModule } from '@containers/gis-settings/gis-settings.module'
import { PathSettingsModule } from '@containers/path-settings/path-settings.module'
import { DigitizationModule } from '@containers/digitization/digitization.module'
import { UnitsSettingsModule } from '@containers/units-settings/units-settings.module'
import { LayerModule } from '@containers/layer/layer.module'
import { MobileUsersModule } from '@containers/mobile-users/mobile-users.module'
import { MobileSettingsModule } from '@containers/mobile-settings/mobile-settings.module'
import { StaticContentTemplatesModule } from '@containers/static-content-templates/static-content-templates.module'
import { MobileTasksModule } from '@containers/mobile-tasks/mobile-tasks.module'
import { LicenseGenerationSettingsModule } from '@containers/license-generation-settings/license-generation-settings.module'
import { UISettingsModule } from '@containers/ui-settings/ui-settings.module'
import { CompoundsModule } from '@containers/compounds/compounds.module'
import { PropertiesTabsModule } from '@components/properties-tabs/properties-tabs.module'
import { LicensesTabsModule } from '@components/licenses-tabs/licenses-tabs.module'
import { LicenseTypesModule } from '@containers/license-types/license-types.module'
import {
  NgClickOutsideDirective,
  NgClickOutsideExcludeDirective,
} from 'ng-click-outside2'
import { NotDeliveredReasonModule } from '@containers/not-delivered-reason/not-delivered-reason.module'
import { ExternalDashboardsSettingsModule } from '@containers/external-dashboard-settings/external-dashboard-settings.module'
import { ExternalDashboardsModule } from '@containers/external-dashboards/external-dashboards.module'
import { ExternalReportsModule } from '@containers/external-reports/external-reports.module'
import { ExternalMobileDashboardModule } from '@containers/external-mobile-dashboard/external-mobile-dashboard.module'
import { FormPostprocessorsModule } from '@containers/form-postprocessors/form-postprocessors.module'
import { CompoundSettingsModule } from '@containers/compound-settings/compound-settings.module'
import { MapAnalyticsModule } from '@containers/map-analytics/map-analytics.module'
import { VersionModule } from '@containers/version/version.module'

export function getMetaReducers(
  auth: AuthService,
  router: Router,
): MetaReducer<any>[] {
  return [refreshToken(auth, router)]
}

export function initializeApp(sharedService: SharedService) {
  return (): Promise<any> => {
    return sharedService.initialize()
  }
}

export function HttpLoaderFactory(i18nService: I18nService) {
  return new MoptaxTranslateLoader(i18nService)
}

@NgModule({
  declarations: [AppComponent, AuthLayoutComponent, MainLayoutComponent],
  imports: [
    // NgIdleModule.forRoot(),
    // NgIdleKeepaliveModule.forRoot(),
    PropertiesTabsModule,
    LicensesTabsModule,
    NgxPermissionsModule,
    RouterModule.forRoot(routes),
    SweetAlert2Module.forRoot(),
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
    }),
    BrowserModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    StoreDevtoolsModule.instrument(),
    AuthModule,
    FormsModule,
    DashboardModule,
    UsersModule,
    StreetsModule,
    StreetTypesModule,
    ContactTypesModule,
    ImageTypesModule,
    FormsModule,
    CommunitiesModule,
    ContactTitlesModule,
    OrganizationsModule,
    IndustriesModule,
    PropertiesModule,
    TemplatesModule,
    DataCyclesModule,
    SettingsModule,
    PropertyTypeTemplatesModule,
    PdfBatchGenerationModule,
    AdminModule,
    DocumentTypesModule,
    BusinessesModule,
    RolesModule,
    FinancialInstitutionsModule,
    PaymentPurposesModule,
    CommercialLicenseMappingsModule,
    MapModule,
    AuditLogsModule,
    FixedTemplatesModule,
    ReportsModule,
    WardsModule,
    SharedModule,
    SearchModule,
    DiscountGroupsModule,
    ProfileModule,
    I18nModule,
    LanguagesModule,
    ContentEditableModule,
    BusinessCategoriesModule,
    SystemSettingsModule,
    SecuritySettingsModule,
    SmtpSettingsModule,
    GisSettingsModule,
    PathSettingsModule,
    UnitsSettingsModule,
    DigitizationModule,
    LayerModule,
    MobileUsersModule,
    MobileSettingsModule,
    StaticContentTemplatesModule,
    MobileTasksModule,
    LicenseGenerationSettingsModule,
    UISettingsModule,
    CompoundsModule,
    LicenseTypesModule,
    NotDeliveredReasonModule,
    NgClickOutsideDirective,
    NgClickOutsideExcludeDirective,
    ExternalDashboardsSettingsModule,
    ExternalDashboardsModule,
    ExternalReportsModule,
    ExternalMobileDashboardModule,
    FormPostprocessorsModule,
    CompoundSettingsModule,
    MapAnalyticsModule,
    VersionModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [I18nService],
      },
      missingTranslationHandler: {
        provide: ngxMissingTranslationHandler,
        useClass: MissingTranslationHandlerService,
      },
      useDefaultLang: false,
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [SharedService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: i18nInitialaizer,
      deps: [Store, I18nService, TranslateService, LanguageService],
      multi: true,
    },
    ...services,
    AuthGuard,
    RedirectGuard,
    GuestGuard,
    PermissionsGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    /*    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },*/
    {
      provide: USER_PROVIDED_META_REDUCERS,
      deps: [AuthService],
      useFactory: refreshToken,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
